import { Link, useNavigate } from '@remix-run/react'
import { LucideUserCircle, LucideZoomIn, LucideZoomOut } from 'lucide-react'
import { useMemo } from 'react'
import {
  aMapFilters,
  aMapRef,
  aMapStyle,
  aOrganization,
  aPersistedFilters,
  aSelectedListing,
  aUser,
  useAtom,
  useAtomValue,
  useSetAtom,
} from '~/atoms'
import {
  ControlTooltip,
  DEFAULT_GOOGLE_CAMERA_ANIMATION_DURATION_MS,
} from '~/components'
import { Avatar, Button } from '~/components/ui'
import { useEmbed, useIsSafari, useMapCardSizes } from '~/hooks'
import {
  cn,
  DEFAULT_VIEW_STATE,
  merge,
  type DefaultMapViewState,
} from '~/utils'
import MapHome from './MapHome'

export default function MapViewControls({
  className,
  isReport = false,
}: {
  className?: string
  isReport?: boolean
}) {
  const organization = useAtomValue(aOrganization)
  const user = useAtomValue(aUser)
  const setSelectedListing = useSetAtom(aSelectedListing)
  const [mapStyle, setMapStyle] = useAtom(aMapStyle)
  const mapFilters = useAtomValue(aMapFilters)
  const setMapFilters = useSetAtom(aMapFilters)
  const mapCardSizes = useMapCardSizes()
  const { initialViewState: embedInitialViewState, isEmbed } = useEmbed()
  const currentMap = useAtomValue(aMapRef)
  const navigate = useNavigate()
  const isSafari = useIsSafari()
  const setPersistedFilters = useSetAtom(aPersistedFilters)

  const setInitialView = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e.preventDefault()
    setSelectedListing(null)
    navigate('/')

    if (!currentMap) return

    const initialViewState = merge<DefaultMapViewState>(
      DEFAULT_VIEW_STATE,
      organization!.options.initialViewState,
      embedInitialViewState ?? {},
      user?.options?.map?.initialViewState ?? {}
    )

    currentMap.flyTo({
      zoom: initialViewState.zoom,
      center: [initialViewState.longitude, initialViewState.latitude],
      padding: { left: mapCardSizes.list, top: 0, right: 0, bottom: 0 },
      bearing: 0,
      duration: DEFAULT_GOOGLE_CAMERA_ANIMATION_DURATION_MS,
      pitch: 0,
    })
  }

  const zoomIn = (e: any) => {
    e.preventDefault()
    if (!currentMap) return

    currentMap.flyTo({
      zoom: (currentMap.getZoom() ?? DEFAULT_VIEW_STATE.zoom) + 1,
      center: currentMap.getCenter(),
      padding: { left: mapCardSizes.list, top: 0, right: 0, bottom: 0 },
      bearing: 0,
      pitch: 0,
      duration: DEFAULT_GOOGLE_CAMERA_ANIMATION_DURATION_MS,
    })
  }

  const zoomOut = (e: any) => {
    e.preventDefault()
    if (!currentMap) return

    currentMap.flyTo({
      zoom: (currentMap.getZoom() ?? DEFAULT_VIEW_STATE.zoom) - 1,
      center: currentMap.getCenter(),
      padding: { left: mapCardSizes.list, top: 0, right: 0, bottom: 0 },
      bearing: 0,
      pitch: 0,
      duration: DEFAULT_GOOGLE_CAMERA_ANIMATION_DURATION_MS,
    })
  }

  const isBuildingToggled = useMemo(() => {
    return (
      (mapFilters.b && mapFilters.b != '0') ||
      (mapFilters.p && mapFilters.p != '0')
    )
  }, [mapFilters])

  const toggleBuildingView = (e: any) => {
    e.preventDefault()
    if (!currentMap) return

    if (
      (currentMap.getPitch?.() ?? 0) !== 0 ||
      (currentMap.getBearing?.() ?? 0) !== 0
    ) {
      const nextFilters = {
        ...mapFilters,
        b: '0',
        p: '0',
      }

      currentMap.flyTo({
        zoom: currentMap.getZoom(),
        center: currentMap.getCenter(),
        duration: DEFAULT_GOOGLE_CAMERA_ANIMATION_DURATION_MS,
        pitch: 0,
        bearing: 0,
      })
      setMapFilters(nextFilters)
      setPersistedFilters(nextFilters)
    } else {
      const nextFilters = {
        ...mapFilters,
        b: '0',
        p: '45',
      }

      setMapStyle('street')
      currentMap.flyTo({
        zoom: currentMap.getZoom(),
        center: currentMap.getCenter(),
        duration: DEFAULT_GOOGLE_CAMERA_ANIMATION_DURATION_MS,
        pitch: 45,
      })
      setMapFilters(nextFilters)
      setPersistedFilters(nextFilters)
    }
  }

  const fallback = useMemo(() => {
    if (!user) {
      return null
    }
    return [user!.firstName.slice(0, 1), user!.lastName.slice(0, 1)].join('')
  }, [user])

  return (
    <div className={className}>
      {!isReport && (
        <>
          {!isEmbed && (
            <ControlTooltip
              text={user ? 'Go to admin portal' : 'Log in'}
              side="bottom">
              <Button asChild variant="map" size="map" className="z-50">
                <Link
                  viewTransition
                  id="map-admin"
                  className={cn(user && user.meta?.photo && 'p-0')}
                  to={user ? '/admin' : '/login'}>
                  {user ? (
                    <Avatar className="h-10 w-10 rounded-md">
                      {user!.meta?.photo && (
                        <Avatar.Image
                          src={user!.meta.photo.thumbUrl}
                          alt={`${user!.firstName} ${user!.lastName}`}
                        />
                      )}
                      <Avatar.Fallback className="rounded-none bg-transparent">
                        {fallback}
                      </Avatar.Fallback>
                    </Avatar>
                  ) : (
                    <LucideUserCircle className="h-5 w-5" />
                  )}
                </Link>
              </Button>
            </ControlTooltip>
          )}

          <MapHome
            mapRef={{ current: currentMap }}
            setInitialView={setInitialView}
            user={user}
          />
        </>
      )}

      <ControlTooltip text="Zoom in">
        <Button
          id="map-zoom-in"
          variant="map"
          size="map"
          className="z-50"
          onClick={zoomIn}>
          <LucideZoomIn className="h-5 w-5" />
        </Button>
      </ControlTooltip>
      <ControlTooltip text="Zoom out">
        <Button
          id="map-zoom-out"
          variant="map"
          size="map"
          className="z-50"
          onClick={zoomOut}>
          <LucideZoomOut className="h-5 w-5" />
        </Button>
      </ControlTooltip>
      {/*{!isSafari && (
        <ControlTooltip text="Toggle buildings view">
          <Button
            id="map-buildings"
            variant="map"
            size="map"
            className={cn(isBuildingToggled && 'z-50 bg-primary text-white')}
            onClick={toggleBuildingView}>
            <LucideBuilding2 className="h-5 w-5" />
          </Button>
        </ControlTooltip>
      )}*/}

      <ControlTooltip
        text={mapStyle === 'street' ? 'View satellite map' : 'View street map'}>
        <Button
          id="map-style"
          variant="map"
          size="map"
          // disabled={!mapStyleLoaded}
          className="z-50"
          onClick={() => {
            setMapStyle(mapStyle === 'street' ? 'satellite' : 'street')
          }}>
          <img
            className="w-full rounded-md"
            src={mapStyle === 'street' ? '/satellite.png' : '/street.png'}
            alt={mapStyle === 'street' ? 'Select Satellite' : 'Select Street'}
          />
        </Button>
      </ControlTooltip>
    </div>
  )
}
